/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { useState, useEffect, useRef } from 'react';
import { Box, FormHelperText } from '@material-ui/core';

import { FileUploader } from './FileUploader';
import microcopyGroupIds from './microcopyGroupIds';
import ProgressBar from '../progress-bar';
import useContentMgmt, {
  PageTextContentType,
} from '../../hooks/useContentMgmt';
import { useImageOcr } from '../../hooks/pge-plus/useImageOcr';
import {
  OcrDocumentType,
  OcrFieldNames,
  StoreFileInput,
} from '../../__generated__/pgeplus-types';

type Props = {
  placeholder: React.ReactNode;
  reuploadLabel: string;
  acceptMimeTypes?: Record<string, []>;

  /* For Serial Number, you can use ‘SerialNumber' except if it’s a Juicebox charger in which case, you should use ‘JuiceboxId'
    For Model Number, you can use ‘ModelNumber' except if it’s a Juicebox charger in which case, you should use ‘SKU’.
    And for Vin, it’s simply 'vin'. */
  ocrType: OcrFieldNames;
  image?: StoreFileInput | undefined; //S3 file object name for already uploaded image. eg., a72a7e65-25e3-4f7a-8f02-2b10febf92d3.jpg
  onComplete: (
    pgeStorekey: string,
    extractedText: string,
    message: string,
    ocrStatus: string,
  ) => void;
};
const FileUploaderWithOCR: React.FC<Props> = ({
  placeholder,
  reuploadLabel,
  image,
  acceptMimeTypes,
  ocrType,
  onComplete,
}) => {
  const [uploadedImage, setUploadedImage] = useState(() => image?.id);

  const { content } = useContentMgmt(microcopyGroupIds.PGE_PLUS_OCR);
  const contentCopy = useRef<PageTextContentType | undefined>();
  contentCopy.current = content;

  const ocrExtrationType = useRef<OcrFieldNames | undefined>();
  ocrExtrationType.current = ocrType;

  const { extractText, isLoading } = useImageOcr();

  useEffect(() => {
    setUploadedImage(image?.id);
  }, [image]);

  useEffect(() => {
    ocrExtrationType.current = ocrType;
  }, [ocrType]);

  const extractTextFromImage = async (fileKey: string) => {
    if (!fileKey) {
      onComplete(
        fileKey,
        '',
        contentCopy?.current?.get('OCR_PROCESS_FAIL_MESSAGE')!,
        'fail',
      );
      return;
    }

    try {
      const ocrCurrentType = ocrExtrationType.current as OcrFieldNames;

      /* For Serial Number, you can use ‘SerialNumber' except if it’s a Juicebox charger in which case, you should use ‘JuiceboxId'
      For Model Number, you can use ‘ModelNumber' except if it’s a Juicebox charger in which case, you should use ‘SKU’.
      And for Vin, it’s simply 'vin'. */
      const fieldNamesToExtract =
        ocrCurrentType === OcrFieldNames.ModelNumber
          ? [OcrFieldNames.Sku, ocrCurrentType]
          : [ocrCurrentType];

      const docType =
        ocrCurrentType === OcrFieldNames.Vin
          ? OcrDocumentType.Vin
          : OcrDocumentType.Evcharger;
      const data = await extractText(fileKey, fieldNamesToExtract, docType);

      let extractedText = data?.textExtracts.find(
        item => item.fieldName === ocrCurrentType,
      );

      //We don't know yet if it is a JuiceBox or a different charger (For juiceBox we use SKU, other chargers we use ModelNumber)
      //Hack for model number OCR is to check if API detectes SKU in case juicebox else we are using model number.
      if (ocrCurrentType === OcrFieldNames.ModelNumber) {
        const extractedTextForJuiceboxSKU = data?.textExtracts.find(
          item => item.fieldName === OcrFieldNames.Sku,
        );

        if (extractedTextForJuiceboxSKU?.value) {
          extractedText = extractedTextForJuiceboxSKU;
        }
      }

      if (data && extractedText?.value) {
        onComplete(
          fileKey,
          extractedText?.value || '',
          contentCopy?.current?.get('OCR_PROCESS_COMPLETION_MESSAGE')!,
          'success',
        );
      } else {
        onComplete(
          fileKey,
          '',
          contentCopy?.current?.get('OCR_PROCESS_FAIL_MESSAGE')!,
          'fail',
        );
      }
    } catch {
      onComplete(
        fileKey,
        '',
        contentCopy?.current?.get('OCR_PROCESS_FAIL_MESSAGE')!,
        'fail',
      );
    }
  };

  return (
    <Box mt={2}>
      <FileUploader
        placeholder={placeholder}
        reuploadLabel={reuploadLabel}
        onUpload={fileKey => {
          setUploadedImage(fileKey);
          void extractTextFromImage(fileKey);
        }}
        image={image?.id}
        acceptMimeTypes={acceptMimeTypes}
      />

      {uploadedImage !== '' && isLoading && (
        <FormHelperText error component="div">
          {content.get('OCR_PROCESS_IN_PROGRESS_MESSAGE')}
          <ProgressBar />
        </FormHelperText>
      )}
    </Box>
  );
};

export default FileUploaderWithOCR;
